import React, { useState, useRef } from 'react';
import { Upload, FileSpreadsheet, AlertCircle, CheckCircle, XCircle, Download, X } from 'lucide-react';
import { parseExcelFile, mapExcelToDto, autoDetectMapping, validateMapping } from '../utils/excelParser';
import type { BulkImportRequest, BulkImportResponse } from '../types';
import { Dialog } from '../components/Dialog';

interface EntityField {
  name: string;
  label: string;
  required: boolean;
}

interface BulkImportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  entityType: 'products' | 'customers' | 'farmers';
  fields: EntityField[];
  onImport: (request: BulkImportRequest<any>) => Promise<BulkImportResponse>;
}

type Step = 'upload' | 'mapping' | 'configure' | 'results';

export const BulkImportDialog: React.FC<BulkImportDialogProps> = ({
  isOpen,
  onClose,
  entityType,
  fields,
  onImport,
}) => {
  const [step, setStep] = useState<Step>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [excelHeaders, setExcelHeaders] = useState<string[]>([]);
  const [excelRows, setExcelRows] = useState<Record<string, any>[]>([]);
  const [fieldMapping, setFieldMapping] = useState<Record<string, string>>({});
  const [conflictStrategy, setConflictStrategy] = useState<'skip' | 'replace'>('skip');
  const [isProcessing, setIsProcessing] = useState(false);
  const [results, setResults] = useState<BulkImportResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const entityLabels = {
    products: 'Productos',
    customers: 'Clientes',
    farmers: 'Agricultores',
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    setError(null);
    setFile(selectedFile);

    try {
      const parsed = await parseExcelFile(selectedFile);
      setExcelHeaders(parsed.headers);
      setExcelRows(parsed.rows);
      
      // Auto-detect mapping
      const autoMapping = autoDetectMapping(parsed.headers, fields);
      setFieldMapping(autoMapping);
      
      setStep('mapping');
    } catch (err: any) {
      setError(err.message || 'Error al procesar el archivo');
      setFile(null);
    }
  };

  const handleMappingChange = (excelColumn: string, dtoField: string) => {
    setFieldMapping(prev => ({
      ...prev,
      [excelColumn]: dtoField
    }));
  };

  const handleNextFromMapping = () => {
    const requiredFields = fields.filter(f => f.required).map(f => f.name);
    const validation = validateMapping(fieldMapping, requiredFields);
    
    if (!validation.valid) {
      setError(`Faltan campos obligatorios: ${validation.missingFields.map(f => fields.find(field => field.name === f)?.label).join(', ')}`);
      return;
    }
    
    setError(null);
    setStep('configure');
  };

  const handleImport = async () => {
    setIsProcessing(true);
    setError(null);

    try {
      // Map Excel rows to DTOs
      const items = mapExcelToDto(excelRows, fieldMapping);
      
      // Call import API
      const response = await onImport({
        conflictStrategy,
        items
      });
      
      setResults(response);
      setStep('results');
    } catch (err: any) {
      setError(err.message || 'Error al importar los datos');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    setStep('upload');
    setFile(null);
    setExcelHeaders([]);
    setExcelRows([]);
    setFieldMapping({});
    setConflictStrategy('skip');
    setResults(null);
    setError(null);
    onClose();
  };

  const getMappedFieldsCount = () => {
    return new Set(Object.values(fieldMapping).filter(v => v)).size;
  };

  return (
    <Dialog isOpen={isOpen} onClose={handleClose} size="lg" noPadding>
        {/* Header */}
        <div className="bg-green-600 text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileSpreadsheet size={24} />
            <div>
              <h2 className="text-xl font-bold">Importar {entityLabels[entityType]}</h2>
              <p className="text-sm text-green-100">Desde archivo Excel</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-green-700 rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="bg-slate-50 px-6 py-3 border-b border-slate-200">
          <div className="flex items-center justify-between max-w-2xl mx-auto">
            {[
              { key: 'upload', label: 'Subir Archivo' },
              { key: 'mapping', label: 'Mapear Columnas' },
              { key: 'configure', label: 'Configurar' },
              { key: 'results', label: 'Resultados' }
            ].map((s, idx) => (
              <div key={s.key} className="flex items-center">
                <div className={`flex items-center gap-2 ${
                  step === s.key ? 'text-green-600 font-semibold' : 
                  ['upload', 'mapping', 'configure', 'results'].indexOf(step) > idx ? 'text-green-500' : 'text-slate-400'
                }`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${
                    step === s.key ? 'bg-green-600 text-white' :
                    ['upload', 'mapping', 'configure', 'results'].indexOf(step) > idx ? 'bg-green-100 text-green-600' : 'bg-slate-200'
                  }`}>
                    {idx + 1}
                  </div>
                  <span className="hidden sm:inline text-sm">{s.label}</span>
                </div>
                {idx < 3 && <div className="w-12 h-0.5 bg-slate-200 mx-2" />}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 flex items-start gap-3">
              <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
              <div className="flex-1">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          )}

          {/* Step 1: Upload */}
          {step === 'upload' && (
            <div className="max-w-xl mx-auto">
              <div className="text-center mb-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Selecciona un archivo Excel</h3>
                <p className="text-sm text-slate-600">Formatos soportados: .xlsx, .xls</p>
              </div>

              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-slate-300 rounded-lg p-12 text-center cursor-pointer hover:border-green-500 hover:bg-green-50 transition-colors"
              >
                <Upload className="mx-auto mb-4 text-slate-400" size={48} />
                <p className="text-slate-700 font-medium mb-1">
                  {file ? file.name : 'Haz clic para seleccionar un archivo'}
                </p>
                <p className="text-sm text-slate-500">o arrastra y suelta aquí</p>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                className="hidden"
              />

              <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800 font-medium mb-2">Formato del archivo:</p>
                <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
                  <li>La primera fila debe contener los nombres de las columnas</li>
                  <li>Cada fila siguiente representa un registro</li>
                  <li>Las celdas vacías se ignorarán</li>
                </ul>
              </div>
            </div>
          )}

          {/* Step 2: Mapping */}
          {step === 'mapping' && (
            <div>
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Mapear Columnas del Excel</h3>
                <p className="text-sm text-slate-600">
                  Asigna cada columna del Excel a un campo de {entityLabels[entityType].toLowerCase()}
                </p>
                <p className="text-sm text-green-600 mt-1">
                  {getMappedFieldsCount()} de {fields.length} campos mapeados
                </p>
              </div>

              <div className="space-y-3">
                {fields.map(field => (
                  <div key={field.name} className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        {field.label} {field.required && <span className="text-red-500">*</span>}
                      </label>
                      <select
                        value={Object.entries(fieldMapping).find(([_, v]) => v === field.name)?.[0] || ''}
                        onChange={(e) => {
                          // Remove old mapping for this field
                          const newMapping = { ...fieldMapping };
                          Object.keys(newMapping).forEach(key => {
                            if (newMapping[key] === field.name) delete newMapping[key];
                          });
                          // Add new mapping
                          if (e.target.value) {
                            newMapping[e.target.value] = field.name;
                          }
                          setFieldMapping(newMapping);
                        }}
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      >
                        <option value="">-- No mapear --</option>
                        {excelHeaders.map(header => (
                          <option key={header} value={header}>{header}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                ))}
              </div>

              {/* Preview */}
              {excelRows.length > 0 && (
                <div className="mt-6">
                  <h4 className="text-sm font-semibold text-slate-700 mb-2">Vista previa (primeras 3 filas)</h4>
                  <div className="overflow-x-auto border border-slate-200 rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100">
                        <tr>
                          {excelHeaders.map(header => (
                            <th key={header} className="px-3 py-2 text-left text-slate-700 font-medium">
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {excelRows.slice(0, 3).map((row, idx) => (
                          <tr key={idx}>
                            {excelHeaders.map(header => (
                              <td key={header} className="px-3 py-2 text-slate-600">
                                {row[header] !== null && row[header] !== undefined ? String(row[header]) : '-'}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Configure */}
          {step === 'configure' && (
            <div className="max-w-xl mx-auto">
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Configurar Importación</h3>
                <p className="text-sm text-slate-600">
                  Se importarán {excelRows.length} registros
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Estrategia de Conflicto
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-start gap-3 p-3 border-2 border-slate-200 rounded-lg cursor-pointer hover:border-green-500 transition-colors">
                      <input
                        type="radio"
                        name="strategy"
                        value="skip"
                        checked={conflictStrategy === 'skip'}
                        onChange={(e) => setConflictStrategy(e.target.value as 'skip')}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="font-medium text-slate-900">Omitir (Skip)</div>
                        <div className="text-sm text-slate-600">
                          Si un registro ya existe (mismo ID externo), se omitirá y se mantendrá el existente
                        </div>
                      </div>
                    </label>

                    <label className="flex items-start gap-3 p-3 border-2 border-slate-200 rounded-lg cursor-pointer hover:border-green-500 transition-colors">
                      <input
                        type="radio"
                        name="strategy"
                        value="replace"
                        checked={conflictStrategy === 'replace'}
                        onChange={(e) => setConflictStrategy(e.target.value as 'replace')}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="font-medium text-slate-900">Reemplazar (Replace)</div>
                        <div className="text-sm text-slate-600">
                          Si un registro ya existe, se actualizará con los nuevos datos del Excel
                        </div>
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Results */}
          {step === 'results' && results && (
            <div className="max-w-2xl mx-auto">
              <div className="text-center mb-6">
                <div className={`w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center ${
                  results.failed === 0 ? 'bg-green-100' : 'bg-yellow-100'
                }`}>
                  {results.failed === 0 ? (
                    <CheckCircle className="text-green-600" size={32} />
                  ) : (
                    <AlertCircle className="text-yellow-600" size={32} />
                  )}
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">
                  {results.failed === 0 ? '¡Importación Completada!' : 'Importación Completada con Errores'}
                </h3>
                <p className="text-slate-600">
                  {results.failed === 0 
                    ? 'Todos los registros se procesaron correctamente'
                    : `${results.totalItems - results.failed} de ${results.totalItems} registros procesados correctamente`
                  }
                </p>
              </div>

              {/* Statistics */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-blue-700">{results.created}</div>
                  <div className="text-sm text-blue-600">Creados</div>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-green-700">{results.updated}</div>
                  <div className="text-sm text-green-600">Actualizados</div>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-yellow-700">{results.skipped}</div>
                  <div className="text-sm text-yellow-600">Omitidos</div>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-red-700">{results.failed}</div>
                  <div className="text-sm text-red-600">Fallidos</div>
                </div>
              </div>

              {/* Errors */}
              {results.errors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <XCircle className="text-red-600" size={20} />
                    <h4 className="font-semibold text-red-900">Errores Encontrados</h4>
                  </div>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {results.errors.map((err, idx) => (
                      <div key={idx} className="text-sm text-red-800 bg-white rounded p-2">
                        <span className="font-medium">Fila {err.index + 2}</span>
                        {err.externalId && <span className="text-red-600"> ({err.externalId})</span>}
                        : {err.message}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-slate-50 px-6 py-4 border-t border-slate-200 flex items-center justify-between">
          <div>
            {step !== 'upload' && step !== 'results' && (
              <button
                onClick={() => {
                  if (step === 'mapping') setStep('upload');
                  if (step === 'configure') setStep('mapping');
                }}
                className="px-4 py-2 text-slate-700 hover:bg-slate-200 rounded-lg transition-colors font-medium"
              >
                Atrás
              </button>
            )}
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-slate-700 hover:bg-slate-200 rounded-lg transition-colors font-medium"
            >
              {step === 'results' ? 'Cerrar' : 'Cancelar'}
            </button>
            {step === 'mapping' && (
              <button
                onClick={handleNextFromMapping}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors font-medium"
              >
                Siguiente
              </button>
            )}
            {step === 'configure' && (
              <button
                onClick={handleImport}
                disabled={isProcessing}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isProcessing && (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                )}
                {isProcessing ? 'Importando...' : 'Importar'}
              </button>
            )}
          </div>
        </div>
    </Dialog>
  );
};
